import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, firebaseConfig } from '../firebaseConfig';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    IoArrowBack,
    IoPersonOutline,
    IoDocumentAttachOutline,
    IoSaveOutline,
    IoCloudUploadOutline,
    IoCheckmarkCircleOutline,
    IoTimeOutline
} from 'react-icons/io5';
import './FormularioEmpleadoScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function FormularioEmpleadoScreen() {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const { currentUser, userData } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Contexto SaaS
    const isSaaS = location.pathname.includes('/superuser/hr');
    const basePath = isSaaS ? '/superuser/hr' : '';
    const activeCondominioId = isSaaS ? 'SAAS' : (userData?.condominioId || 'GLOBAL');

    // Fixed gradient for the banner (Teal)
    const gradient = 'linear-gradient(135deg, #0d9488 0%, #115e59 100%)';

    // Modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalTitle, setModalTitle] = useState('');

    // Helper de Permisos
    const checkPermiso = (permiso) => {
        if (userData?.rol === 'superuser' || userData?.rol === 'masteruser') {
            return !!userData?.permisos?.[permiso];
        }
        return true; 
    };

    // Route Protection
    useEffect(() => {
        if (userData) {
            if (!isEditMode && !checkPermiso('crear_empleados')) {
                alert('No tienes permiso para crear empleados.');
                navigate(`${basePath}/gestion-empleados`);
            } else if (isEditMode && !checkPermiso('editar_empleados')) {
                alert('No tienes permiso para editar empleados.');
                navigate(`${basePath}/gestion-empleados`);
            }
        }
    }, [userData, isEditMode, navigate, basePath]);



    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);

    // Basic Data
    const [nombre, setNombre] = useState('');
    const [apellidos, setApellidos] = useState('');
    const [rfc, setRfc] = useState('');
    const [curp, setCurp] = useState('');
    const [puesto, setPuesto] = useState('');
    const [email, setEmail] = useState('');
    const [contrasenaTemporal, setContrasenaTemporal] = useState('');

    // Financial/Payroll Data
    const [salarioBruto, setSalarioBruto] = useState('');
    const [periodoPago, setPeriodoPago] = useState('Quincenal');
    const [fechaIngreso, setFechaIngreso] = useState('');
    const [banco, setBanco] = useState('');
    const [clabe, setClabe] = useState('');

    // Documents (URLs)
    const [ineUrl, setIneUrl] = useState('');
    const [comprobanteUrl, setComprobanteUrl] = useState('');
    const [contratoUrl, setContratoUrl] = useState('');

    // Schedule / Horario Data
    const [horariosDetalle, setHorariosDetalle] = useState({
      Lunes: { activo: true, entrada: '09:00', salida: '18:00' },
      Martes: { activo: true, entrada: '09:00', salida: '18:00' },
      Miércoles: { activo: true, entrada: '09:00', salida: '18:00' },
      Jueves: { activo: true, entrada: '09:00', salida: '18:00' },
      Viernes: { activo: true, entrada: '09:00', salida: '18:00' },
      Sábado: { activo: false, entrada: '09:00', salida: '14:00' },
      Domingo: { activo: false, entrada: '09:00', salida: '14:00' }
    });

    useEffect(() => {
        if (isEditMode && currentUser) {
            const fetchEmpleado = async () => {
                try {
                    const snap = await getDoc(doc(db, "empleados", id));
                    if (snap.exists()) {
                        const data = snap.data();
                        setNombre(data.nombre || '');
                        setApellidos(data.apellidos || '');
                        setRfc(data.rfc || '');
                        setCurp(data.curp || '');
                        setPuesto(data.puesto || '');
                        setSalarioBruto(data.salarioBruto || '');
                        setPeriodoPago(data.periodoPago || 'Quincenal');
                        setFechaIngreso(data.fechaIngreso || '');
                        setBanco(data.banco || '');
                        setClabe(data.clabe || '');
                        setIneUrl(data.ineUrl || '');
                        setComprobanteUrl(data.comprobanteUrl || '');
                        setContratoUrl(data.contratoUrl || '');
                        setEmail(data.email || '');
                        if (data.horariosDetalle) {
                            setHorariosDetalle(data.horariosDetalle);
                        } else {
                            const base = {
                              Lunes: { activo: false, entrada: data.horaEntrada || '09:00', salida: data.horaSalida || '18:00' },
                              Martes: { activo: false, entrada: data.horaEntrada || '09:00', salida: data.horaSalida || '18:00' },
                              Miércoles: { activo: false, entrada: data.horaEntrada || '09:00', salida: data.horaSalida || '18:00' },
                              Jueves: { activo: false, entrada: data.horaEntrada || '09:00', salida: data.horaSalida || '18:00' },
                              Viernes: { activo: false, entrada: data.horaEntrada || '09:00', salida: data.horaSalida || '18:00' },
                              Sábado: { activo: false, entrada: '09:00', salida: '14:00' },
                              Domingo: { activo: false, entrada: '09:00', salida: '14:00' }
                            };
                            if (data.diasLaborales) {
                                data.diasLaborales.forEach(d => {
                                    if (base[d]) base[d].activo = true;
                                });
                            }
                            setHorariosDetalle(base);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching empleado:", error);
                } finally {
                    setFetching(false);
                }
            };
            fetchEmpleado();
        }
    }, [id, isEditMode, currentUser]);

    const handleFileUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        try {
            const fileRef = ref(storage, `empleados_documentos/${activeCondominioId}/${Date.now()}_${file.name}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);

            if (type === 'ine') setIneUrl(url);
            if (type === 'comprobante') setComprobanteUrl(url);
            if (type === 'contrato') setContratoUrl(url);

            setModalTitle("Archivo Guardado");
            setModalMessage("El documento se ha subido y guardado exitosamente en el expediente.");
            setShowSuccessModal(true);
        } catch (error) {
            console.error("Upload error:", error);
            alert("Error al subir el documento.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!nombre || !apellidos || !puesto || !salarioBruto) {
            alert("Por favor completa los datos obligatorios (Nombre, Apellidos, Puesto, Salario).");
            return;
        }

        if (!isEditMode && (email || contrasenaTemporal)) {
            if (!email || !contrasenaTemporal) {
                alert("Si deseas habilitar el acceso a la app para este trabajador, debes proporcionar tanto el Correo como la Contraseña Temporal.");
                return;
            }
            if (contrasenaTemporal.length < 6) {
                alert("La contraseña temporal debe tener al menos 6 caracteres.");
                return;
            }
        }

        setLoading(true);

        const derivedDias = Object.keys(horariosDetalle).filter(k => horariosDetalle[k].activo);
        const firstActiveDay = derivedDias[0] || 'Lunes';
        const payload = {
            nombre,
            apellidos,
            rfc: rfc.toUpperCase(),
            curp: curp.toUpperCase(),
            puesto,
            salarioBruto: Number(salarioBruto),
            periodoPago,
            fechaIngreso,
            banco,
            clabe,
            ineUrl,
            comprobanteUrl,
            contratoUrl,
            horaEntrada: horariosDetalle[firstActiveDay]?.entrada || '09:00',
            horaSalida: horariosDetalle[firstActiveDay]?.salida || '18:00',
            diasLaborales: derivedDias,
            horariosDetalle,
            condominioId: activeCondominioId,
            estatus: 'Activo',
            updatedAt: new Date()
        };

        try {
            if (isEditMode) {
                await updateDoc(doc(db, "empleados", id), payload);
                setModalTitle("Expediente Guardado");
                setModalMessage(`El expediente de ${nombre} ${apellidos} ha sido actualizado correctamente.`);
            } else {
                let generatedUid = null;
                if (email && contrasenaTemporal) {
                    try {
                        let secApp;
                        if (!getApps().find(a => a.name === 'Secondary')) {
                            secApp = initializeApp(firebaseConfig, 'Secondary');
                        } else {
                            secApp = getApp('Secondary');
                        }
                        const secAuth = getAuth(secApp);
                        const cred = await createUserWithEmailAndPassword(secAuth, email.toLowerCase().trim(), contrasenaTemporal);
                        generatedUid = cred.user.uid;
                        await secAuth.signOut();

                        // Registrar en la coleccion de usuarios para el login
                        await setDoc(doc(db, "usuarios", generatedUid), {
                            uid: generatedUid,
                            nombre: nombre.trim(),
                            apellidos: apellidos.trim(),
                            email: email.toLowerCase().trim(),
                            rol: 'trabajador',
                            condominioId: activeCondominioId,
                            cambiarContrasena: true,
                            estatus: 'Activo',
                            createdAt: new Date()
                        });
                    } catch (authError) {
                        console.error("Auth creation error:", authError);
                        alert("Error al crear la cuenta del usuario: " + authError.message);
                        setLoading(false);
                        return;
                    }
                }

                payload.createdAt = new Date();
                payload.email = email.toLowerCase().trim();
                if (generatedUid) {
                    payload.usuarioId = generatedUid;
                }

                const empRef = await addDoc(collection(db, "empleados"), payload);

                await addDoc(collection(db, `empleados/${empRef.id}/prestaciones`), {
                    tipo: 'Ley',
                    diasAguinaldo: 15,
                    diasVacaciones: 12,
                    primaVacacionalPorcentaje: 25,
                    valesDespensa: 0,
                    fondoAhorro: 0,
                    updatedAt: new Date()
                });

                setModalTitle("Empleado Registrado");
                setModalMessage(`Se registró exitosamente a ${nombre} ${apellidos} con sus prestaciones de Ley.`);
            }
            setShowSuccessModal(true);
        } catch (error) {
            console.error("Save error:", error);
            alert("Error al guardar empleado.");
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        setShowSuccessModal(false);
        if (modalTitle !== "Archivo Guardado") {
            navigate(`${basePath}/gestion-empleados`);
        }
    };

    if (fetching) return <div className="empleado-loading">Cargando datos...</div>;

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/superuser/hr" />
            
            <main className="ks-main">
                <KondoBrandHeader showBack={false} />

                <div className="ks-scroll-area">
                    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
                        
                        {/* Premium Header Banner */}
                        <div style={{
                            background: gradient || 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                            position: 'relative',
                            overflow: 'hidden',
                            padding: '30px 25px',
                            borderRadius: '24px',
                            marginBottom: '30px',
                            boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
                            color: 'white'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', zIndex: 2, position: 'relative' }}>
                                <button
                                    type="button"
                                    onClick={() => navigate(`${basePath}/gestion-empleados`)}
                                    style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        borderRadius: '50%',
                                        width: '44px',
                                        height: '44px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        color: 'white',
                                        flexShrink: 0
                                    }}
                                    className="encuestas-glass-btn"
                                >
                                    <IoArrowBack size={22} />
                                </button>
                                <div>
                                    <h1 style={{ color: 'white', margin: '0 0 6px', fontSize: '1.7rem', fontWeight: '800', fontFamily: "'Manrope', sans-serif" }}>
                                        {isEditMode ? 'Editar Expediente' : 'Alta de Nuevo Empleado'}
                                    </h1>
                                    <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.95rem', fontWeight: '500' }}>
                                        Datos Generales y Salario Base
                                    </p>
                                </div>
                            </div>
                            <div style={{ position: 'absolute', right: '-20px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.12 }}>
                                <IoPersonOutline size={180} color="white" />
                            </div>
                        </div>

                        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                            
                            {/* SECCIÓN 1: DATOS PERSONALES */}
                            <div style={sectionCardStyle}>
                                <h3 style={sectionHeaderStyle}>
                                    <span style={{...iconWrapperStyle, backgroundColor: '#e0f2fe', color: '#0284c7'}}><IoPersonOutline size={18} /></span>
                                    Datos Personales
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    <div>
                                        <label style={labelStyle}>Nombre(s) *</label>
                                        <input type="text" value={nombre} onChange={e => setNombre(e.target.value)} style={inputStyle} required />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Apellidos *</label>
                                        <input type="text" value={apellidos} onChange={e => setApellidos(e.target.value)} style={inputStyle} required />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>RFC</label>
                                        <input type="text" value={rfc} onChange={e => setRfc(e.target.value)} maxLength={13} style={{ ...inputStyle, textTransform: 'uppercase' }} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>CURP</label>
                                        <input type="text" value={curp} onChange={e => setCurp(e.target.value)} maxLength={18} style={{ ...inputStyle, textTransform: 'uppercase' }} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Correo Electrónico (Acceso App)</label>
                                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} disabled={isEditMode} placeholder="ejemplo@kondo.com" />
                                    </div>
                                    {!isEditMode && (
                                        <div>
                                            <label style={labelStyle}>Contraseña Temporal (Acceso App)</label>
                                            <input type="password" value={contrasenaTemporal} onChange={e => setContrasenaTemporal(e.target.value)} style={inputStyle} placeholder="Mínimo 6 caracteres" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* SECCIÓN 2: DATOS LABORALES & NÓMINA */}
                            <div style={sectionCardStyle}>
                                <h3 style={sectionHeaderStyle}>
                                    <span style={{...iconWrapperStyle, backgroundColor: '#dcfce7', color: '#15803d'}}><IoDocumentAttachOutline size={18} /></span>
                                    Datos Laborales & Salario
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                    <div>
                                        <label style={labelStyle}>Puesto / Cargo *</label>
                                        <input type="text" value={puesto} onChange={e => setPuesto(e.target.value)} style={inputStyle} placeholder="Ej. Guardia, Conserje" required />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Fecha de Ingreso</label>
                                        <input type="date" value={fechaIngreso} onChange={e => setFechaIngreso(e.target.value)} style={inputStyle} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Salario Bruto (Antes de Impuestos) *</label>
                                        <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '12px', overflow: 'hidden' }}>
                                            <span style={{ padding: '12px 16px', background: '#f1f5f9', color: '#475569', fontWeight: 'bold', borderRight: '1px solid #cbd5e1' }}>$</span>
                                            <input type="number" step="0.01" value={salarioBruto} onChange={e => setSalarioBruto(e.target.value)} style={{ ...inputStyle, border: 'none', borderRadius: 0, backgroundColor: '#fdfdfd' }} required />
                                        </div>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Periodo de Pago</label>
                                        <select value={periodoPago} onChange={e => setPeriodoPago(e.target.value)} style={selectStyle}>
                                            <option value="Semanal">Semanal</option>
                                            <option value="Catorcenal">Catorcenal</option>
                                            <option value="Quincenal">Quincenal</option>
                                            <option value="Mensual">Mensual</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Banco (Para Dispersión)</label>
                                        <input type="text" value={banco} onChange={e => setBanco(e.target.value)} style={inputStyle} placeholder="Ej. BBVA, Santander" />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>CLABE Interbancaria (18 dígitos)</label>
                                        <input type="text" value={clabe} onChange={e => setClabe(e.target.value)} maxLength={18} style={inputStyle} />
                                    </div>
                                </div>
                            </div>

                            {/* SECCIÓN 3: EXPEDIENTE DIGITAL */}
                            <div style={sectionCardStyle}>
                                <h3 style={sectionHeaderStyle}>
                                    <span style={{...iconWrapperStyle, backgroundColor: '#f3e8ff', color: '#7e22ce'}}><IoCloudUploadOutline size={18} /></span>
                                    Expediente Digital (Archivos)
                                </h3>
                                <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                                    Sube fotos o PDFs (INE, Comprobante) a la nube del Condominio.
                                </p>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                                    <div style={uploadCardStyle}>
                                        <h4 style={uploadCardTitleStyle}>Identificación (INE)</h4>
                                        {ineUrl ? (
                                            <div className="file-success"><a href={ineUrl} target="_blank" rel="noreferrer">Ver Archivo</a></div>
                                        ) : (
                                            <label style={uploadBtnStyle} className="upload-btn">
                                                <input type="file" onChange={e => handleFileUpload(e, 'ine')} hidden accept="image/*,.pdf" />
                                                Subir Archivo
                                            </label>
                                        )}
                                    </div>

                                    <div style={uploadCardStyle}>
                                        <h4 style={uploadCardTitleStyle}>Comprobante de Domicilio</h4>
                                        {comprobanteUrl ? (
                                            <div className="file-success"><a href={comprobanteUrl} target="_blank" rel="noreferrer">Ver Archivo</a></div>
                                        ) : (
                                            <label style={uploadBtnStyle} className="upload-btn">
                                                <input type="file" onChange={e => handleFileUpload(e, 'comprobante')} hidden accept="image/*,.pdf" />
                                                Subir Archivo
                                            </label>
                                        )}
                                    </div>

                                    <div style={uploadCardStyle}>
                                        <h4 style={uploadCardTitleStyle}>Contrato Firmado</h4>
                                        {contratoUrl ? (
                                            <div className="file-success"><a href={contratoUrl} target="_blank" rel="noreferrer">Ver Archivo</a></div>
                                        ) : (
                                            <label style={uploadBtnStyle} className="upload-btn">
                                                <input type="file" onChange={e => handleFileUpload(e, 'contrato')} hidden accept="image/*,.pdf" />
                                                Subir Archivo
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* SECCIÓN 4: JORNADA LABORAL Y HORARIOS */}
                            <div style={sectionCardStyle}>
                                <h3 style={sectionHeaderStyle}>
                                    <span style={{...iconWrapperStyle, backgroundColor: '#fef3c7', color: '#d97706'}}><IoTimeOutline size={18} /></span>
                                    Jornada Laboral y Horario por Día (Recordatorios GPS)
                                </h3>
                                <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                                    Activa los días laborables e ingresa el horario específico de entrada y salida para cada uno (útil para sábados de medio día).
                                </p>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map(dia => {
                                        const diaInfo = horariosDetalle[dia] || { activo: false, entrada: '09:00', salida: '18:00' };
                                        return (
                                            <div key={dia} style={{ 
                                                display: 'grid', 
                                                gridTemplateColumns: '150px 1fr 1fr', 
                                                alignItems: 'center', 
                                                gap: '15px',
                                                padding: '12px',
                                                borderRadius: '8px',
                                                background: diaInfo.activo ? '#faf5ff' : '#f8fafc',
                                                border: diaInfo.activo ? '1px solid #e9d5ff' : '1px solid #e2e8f0'
                                            }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem', color: '#334155' }}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={diaInfo.activo} 
                                                        onChange={e => {
                                                            setHorariosDetalle({
                                                                ...horariosDetalle,
                                                                [dia]: { ...diaInfo, activo: e.target.checked }
                                                            });
                                                        }}
                                                    />
                                                    <span>{dia}</span>
                                                </label>
                                                
                                                <div>
                                                    <label style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '2px' }}>Entrada</label>
                                                    <input 
                                                        type="time" 
                                                        value={diaInfo.entrada} 
                                                        disabled={!diaInfo.activo}
                                                        onChange={e => {
                                                            setHorariosDetalle({
                                                                ...horariosDetalle,
                                                                [dia]: { ...diaInfo, entrada: e.target.value }
                                                            });
                                                        }}
                                                        style={inputStyle}
                                                    />
                                                </div>

                                                <div>
                                                    <label style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '2px' }}>Salida</label>
                                                    <input 
                                                        type="time" 
                                                        value={diaInfo.salida} 
                                                        disabled={!diaInfo.activo}
                                                        onChange={e => {
                                                            setHorariosDetalle({
                                                                ...horariosDetalle,
                                                                [dia]: { ...diaInfo, salida: e.target.value }
                                                            });
                                                        }}
                                                        style={inputStyle}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                <button 
                                    type="submit" 
                                    disabled={loading} 
                                    style={{ 
                                        background: loading ? '#93c5fd' : '#10b981', 
                                        color: 'white', 
                                        border: 'none', 
                                        padding: '14px 32px', 
                                        borderRadius: '14px', 
                                        fontSize: '1rem', 
                                        fontWeight: '700', 
                                        cursor: loading ? 'not-allowed' : 'pointer', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '10px',
                                        boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <IoSaveOutline size={20} />
                                    {loading ? 'Guardando...' : 'Guardar Expediente'}
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </main>

            {/* Custom Success Modal */}
            {showSuccessModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(6px)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 99999
                }}>
                    <div style={{
                        background: 'white', padding: '35px 30px', borderRadius: '28px',
                        width: '90%', maxWidth: '400px', textAlign: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        fontFamily: "'Inter', sans-serif"
                    }}>
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '50%',
                            backgroundColor: '#dcfce7', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px', color: '#16a34a',
                            boxShadow: '0 8px 16px rgba(22, 163, 74, 0.15)'
                        }}>
                            <IoCheckmarkCircleOutline size={36} />
                        </div>
                        <h3 style={{ margin: '0 0 10px', fontSize: '1.4rem', fontWeight: '800', color: '#0f172a' }}>
                            {modalTitle}
                        </h3>
                        <p style={{ margin: '0 0 24px', fontSize: '0.95rem', color: '#475569', lineHeight: '1.5' }}>
                            {modalMessage}
                        </p>
                        <button
                            onClick={handleModalClose}
                            style={{
                                width: '100%', padding: '14px 24px', border: 'none',
                                borderRadius: '14px', background: '#3b82f6', color: 'white',
                                fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.25)',
                                transition: 'all 0.2s'
                            }}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            <KondoBottomNav activeRoute="/superuser/hr" />
        </div>
    );
}

// Internal styles matching the new premium look
const sectionCardStyle = { 
    background: 'white', 
    padding: '28px', 
    borderRadius: '24px', 
    border: '1px solid #f1f5f9', 
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)' 
};

const sectionHeaderStyle = { 
    margin: '0 0 24px', 
    display: 'flex', 
    alignItems: 'center', 
    gap: '12px', 
    color: '#0f172a',
    fontSize: '1.15rem',
    fontWeight: '800',
    fontFamily: "'Manrope', sans-serif"
};

const iconWrapperStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const labelStyle = { 
    display: 'block', 
    fontSize: '0.85rem', 
    color: '#475569', 
    marginBottom: '8px', 
    fontWeight: '600' 
};

const inputStyle = { 
    width: '100%', 
    padding: '12px 16px', 
    border: '1px solid #cbd5e1', 
    borderRadius: '12px', 
    fontSize: '0.95rem', 
    color: '#1e293b', 
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    outline: 'none',
    backgroundColor: '#fafafa'
};

const selectStyle = { 
    ...inputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='%2364748b' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '40px'
};

const uploadCardStyle = {
    border: '2px dashed #cbd5e1',
    borderRadius: '16px',
    padding: '24px',
    textAlign: 'center',
    background: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px'
};

const uploadCardTitleStyle = {
    margin: 0,
    color: '#334155',
    fontSize: '0.95rem',
    fontWeight: '700'
};

const uploadBtnStyle = {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#e2e8f0',
    color: '#475569',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '0.9rem',
    transition: 'all 0.2s'
};

export default FormularioEmpleadoScreen;
